import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PerformanceMetrics from './components/PerformanceMetrics';
import CommissionTracker from './components/CommissionTracker';
import QuickActionsAgent from './components/QuickActionsAgent';
import TerritoryOverview from './components/TerritoryOverview';
import RecentTransactionsAgent from './components/RecentTransactionsAgent';
import MarketingTools from './components/MarketingTools';
import Icon from '../../components/AppIcon';
import CustomerRegistrationModal from './components/CustomerRegistrationModal';
import SendMoneyModal from './components/SendMoneyModal';
import MarketingKitModal from './components/MarketingKitModal';
import CommissionPayoutModal from './components/CommissionPayoutModal';
import CustomerListModal from './components/CustomerListModal';
import TransactionHistoryModal from './components/TransactionHistoryModal';
import RateCalculatorModal from './components/RateCalculatorModal';
import NotificationsModal from './components/NotificationsModal';
import TransactionDetailsModal from './components/TransactionDetailsModal';
import ContactCustomerModal from './components/ContactCustomerModal';
import FlagTransactionModal from './components/FlagTransactionModal';
import RefundTransactionModal from './components/RefundTransactionModal';

const AgentDashboard = () => {
  const location = useLocation();
  const [agent, setAgent] = useState(null);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showSendMoneyModal, setShowSendMoneyModal] = useState(false);
  const [showMarketingKitModal, setShowMarketingKitModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showCustomerListModal, setShowCustomerListModal] = useState(false);
  const [showTransactionHistoryModal, setShowTransactionHistoryModal] = useState(false);
  const [showRateCalculatorModal, setShowRateCalculatorModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showTransactionDetailsModal, setShowTransactionDetailsModal] = useState(false);
  const [showContactCustomerModal, setShowContactCustomerModal] = useState(false);
  const [showFlagTransactionModal, setShowFlagTransactionModal] = useState(false);
  const [showRefundTransactionModal, setShowRefundTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Check if coming from registration
  useEffect(() => {
    const registrationComplete = location?.state?.registrationComplete;
    const agentStatus = location?.state?.agentStatus;
    
    if (registrationComplete) {
      setShowWelcomeMessage(true);
      // Auto-hide welcome message after 5 seconds
      setTimeout(() => setShowWelcomeMessage(false), 5000);
    }

    // Mock agent data - in real app this would come from authentication context
    const mockAgent = {
      id: 'agent_456',
      name: 'Michael Rodriguez',
      email: 'michael.rodriguez@agent.com',
      role: 'agent',
      agentType: 'business', // individual, business, franchise
      status: agentStatus || 'active', // pending_approval, active, suspended
      joinedDate: '2023-08-15',
      territories: ['north-america', 'europe'],
      commissionRate: 3.5,
      performance: {
        monthlyVolume: 185750,
        monthlyTarget: 200000,
        customersAcquired: 47,
        customerTarget: 50,
        successRate: 98.5,
        avgTransactionValue: 875
      },
      commissions: {
        thisMonth: 6501.25,
        lastMonth: 5890.50,
        pending: 1250.75,
        totalEarned: 42567.89,
        nextPayout: '2024-11-22'
      },
      customers: {
        total: 234,
        active: 187,
        newThisMonth: 47,
        retentionRate: 89.5
      },
      territories_details: [
        {
          id: 'north-america',
          name: 'North America',
          performance: 92.5,
          volume: 125000,
          commission: 4375
        },
        {
          id: 'europe',
          name: 'Europe',
          performance: 87.8,
          volume: 60750,
          commission: 2126.25
        }
      ]
    };
    
    setAgent(mockAgent);
  }, [location]);

  const handleLogout = () => {
    setAgent(null);
    // In real app, clear authentication tokens
  };

  const handleOpenRegistration = () => {
    setShowRegistrationModal(true);
  };

  const handleCloseRegistration = () => {
    setShowRegistrationModal(false);
  };

  const handleOpenSendMoney = () => {
    setShowSendMoneyModal(true);
  };

  const handleCloseSendMoney = () => {
    setShowSendMoneyModal(false);
  };

  const handleOpenMarketingKit = () => {
    setShowMarketingKitModal(true);
  };

  const handleCloseMarketingKit = () => {
    setShowMarketingKitModal(false);
  };

  const handleOpenPayout = () => {
    setShowPayoutModal(true);
  };

  const handleClosePayout = () => {
    setShowPayoutModal(false);
  };

  const handleOpenCustomerList = () => {
    setShowCustomerListModal(true);
  };

  const handleCloseCustomerList = () => {
    setShowCustomerListModal(false);
  };

  const handleOpenTransactionHistory = () => {
    setShowTransactionHistoryModal(true);
  };

  const handleCloseTransactionHistory = () => {
    setShowTransactionHistoryModal(false);
  };

  const handleOpenRateCalculator = () => {
    setShowRateCalculatorModal(true);
  };

  const handleCloseRateCalculator = () => {
    setShowRateCalculatorModal(false);
  };

  const handleOpenNotifications = () => {
    setShowNotificationsModal(true);
  };

  const handleCloseNotifications = () => {
    setShowNotificationsModal(false);
  };

  const handleOpenTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetailsModal(true);
  };

  const handleCloseTransactionDetails = () => {
    setShowTransactionDetailsModal(false);
    setSelectedTransaction(null);
  };

  const handleOpenContactCustomer = (transaction) => {
    setSelectedTransaction(transaction);
    setShowContactCustomerModal(true);
  };

  const handleCloseContactCustomer = () => {
    setShowContactCustomerModal(false);
    setSelectedTransaction(null);
  };

  const handleOpenFlagTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowFlagTransactionModal(true);
  };

  const handleCloseFlagTransaction = () => {
    setShowFlagTransactionModal(false);
    setSelectedTransaction(null);
  };

  const handleOpenRefund = (transaction) => {
    setSelectedTransaction(transaction);
    setShowRefundTransactionModal(true);
  };

  const handleCloseRefund = () => {
    setShowRefundTransactionModal(false);
    setSelectedTransaction(null);
  };

  if (!agent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const getStatusInfo = () => {
    switch (agent?.status) {
      case 'active':
        return {
          value: 'Active Agent',
          subtitle: 'All systems operational',
          variant: 'success',
          iconColor: 'var(--color-success)'
        };
      case 'pending_approval':
        return {
          value: 'Pending Approval',
          subtitle: 'Application under review',
          variant: 'warning',
          iconColor: 'var(--color-warning)'
        };
      case 'suspended':
        return {
          value: 'Suspended',
          subtitle: 'Contact support',
          variant: 'error',
          iconColor: 'var(--color-error)'
        };
      default:
        return {
          value: 'Unknown',
          subtitle: 'Contact support',
          variant: 'warning',
          iconColor: 'var(--color-warning)'
        };
    }
  };

  const statusInfo = getStatusInfo();

  // Show different content based on status
  if (agent?.status === 'pending_approval') {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Agent Dashboard - MoneyTransfer Pro</title>
          <meta name="description" content="Monitor your agent performance, track commissions, and manage customers from your comprehensive agent dashboard." />
        </Helmet>
        <Header user={agent} onLogout={handleLogout} />
        
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] px-4 py-8">
          <div className="max-w-lg w-full text-center space-y-6">
            {showWelcomeMessage && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 text-success mb-2">
                  <Icon name="CheckCircle" size={20} />
                  <span className="font-medium">Registration Complete!</span>
                </div>
                <p className="text-sm text-success/70">
                  Welcome to the agent network. Your application is now under review.
                </p>
              </div>
            )}
            
            <div className="w-20 h-20 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
              <Icon name="Clock" size={40} className="text-warning" />
            </div>
            
            <div className="space-y-3">
              <h1 className="text-2xl font-bold text-foreground">Application Under Review</h1>
              <p className="text-muted-foreground">
                Thank you for joining our agent network! Our compliance team is reviewing your 
                application and supporting documents. You'll receive an email notification once 
                your account is approved.
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-foreground">What happens next?</h3>
              <div className="space-y-3 text-sm text-left">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-primary">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Document Verification</p>
                    <p className="text-muted-foreground">Our compliance team reviews all submitted documents</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-primary">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Background Check</p>
                    <p className="text-muted-foreground">Standard regulatory compliance verification</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-primary">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Account Activation</p>
                    <p className="text-muted-foreground">Full agent dashboard access and training materials</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <p className="mb-2"><strong>Expected timeline:</strong> 3-5 business days</p>
              <p>Questions? Contact our agent support team at <strong>agents@moneytransfer.com</strong></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Agent Dashboard - MoneyTransfer Pro</title>
        <meta name="description" content="Monitor your agent performance, track commissions, and manage customers from your comprehensive agent dashboard." />
      </Helmet>
      <Header user={agent} onLogout={handleLogout} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                Welcome back, {agent?.name}
              </h1>
              <p className="text-muted-foreground">
                Track your performance, manage customers, and grow your agent business.
              </p>
            </div>
            <div className="hidden sm:flex items-center space-x-2 text-sm">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">
                Agent since {new Date(agent?.joinedDate)?.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long' 
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Performance Overview */}
        <div className="mb-8">
          <PerformanceMetrics agent={agent} />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <QuickActionsAgent 
            onRegisterCustomer={handleOpenRegistration}
            onSendMoney={handleOpenSendMoney}
            onDownloadKit={handleOpenMarketingKit}
            onRequestPayout={handleOpenPayout}
            onViewNotifications={handleOpenNotifications}
          />
        </div>

        {/* New Quick Access Tools */}
        <div className="mb-8">
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Quick Access Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={handleOpenCustomerList}
                className="flex items-center space-x-3 p-4 bg-background hover:bg-muted rounded-lg border border-border transition-colors"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={20} className="text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Customer List</div>
                  <div className="text-sm text-muted-foreground">View all customers</div>
                </div>
              </button>

              <button
                onClick={handleOpenTransactionHistory}
                className="flex items-center space-x-3 p-4 bg-background hover:bg-muted rounded-lg border border-border transition-colors"
              >
                <div className="w-10 h-10 bg-info/10 rounded-lg flex items-center justify-center">
                  <Icon name="History" size={20} className="text-info" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Transaction History</div>
                  <div className="text-sm text-muted-foreground">Complete history</div>
                </div>
              </button>

              <button
                onClick={handleOpenRateCalculator}
                className="flex items-center space-x-3 p-4 bg-background hover:bg-muted rounded-lg border border-border transition-colors"
              >
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Calculator" size={20} className="text-success" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Rate Calculator</div>
                  <div className="text-sm text-muted-foreground">Check rates & fees</div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            <CommissionTracker agent={agent} onRequestPayout={handleOpenPayout} />
            <RecentTransactionsAgent 
              onSendMoney={handleOpenSendMoney}
              onViewDetails={handleOpenTransactionDetails}
              onContactCustomer={handleOpenContactCustomer}
              onFlagTransaction={handleOpenFlagTransaction}
              onProcessRefund={handleOpenRefund}
            />
          </div>

          {/* Right Column - Side Widgets */}
          <div className="space-y-8">
            <TerritoryOverview agent={agent} />
          </div>
        </div>

        {/* Bottom Section */}
        <div>
          <MarketingTools />
        </div>
      </main>

      {/* Customer Registration Modal */}
      <CustomerRegistrationModal
        isOpen={showRegistrationModal}
        onClose={handleCloseRegistration}
        agentId={agent?.id}
      />

      {/* Send Money Modal */}
      <SendMoneyModal
        isOpen={showSendMoneyModal}
        onClose={handleCloseSendMoney}
        agentId={agent?.id}
      />

      {/* Marketing Kit Modal */}
      <MarketingKitModal
        isOpen={showMarketingKitModal}
        onClose={handleCloseMarketingKit}
      />

      {/* Commission Payout Modal */}
      <CommissionPayoutModal
        isOpen={showPayoutModal}
        onClose={handleClosePayout}
        agent={agent}
      />

      {/* Customer List Modal */}
      <CustomerListModal
        isOpen={showCustomerListModal}
        onClose={handleCloseCustomerList}
        agentId={agent?.id}
      />

      {/* Transaction History Modal */}
      <TransactionHistoryModal
        isOpen={showTransactionHistoryModal}
        onClose={handleCloseTransactionHistory}
        agentId={agent?.id}
      />

      {/* Rate Calculator Modal */}
      <RateCalculatorModal
        isOpen={showRateCalculatorModal}
        onClose={handleCloseRateCalculator}
      />

      {/* Notifications Modal */}
      <NotificationsModal
        isOpen={showNotificationsModal}
        onClose={handleCloseNotifications}
      />

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={showTransactionDetailsModal}
        onClose={handleCloseTransactionDetails}
        transaction={selectedTransaction}
      />

      {/* Contact Customer Modal */}
      <ContactCustomerModal
        isOpen={showContactCustomerModal}
        onClose={handleCloseContactCustomer}
        transaction={selectedTransaction}
      />

      {/* Flag Transaction Modal */}
      <FlagTransactionModal
        isOpen={showFlagTransactionModal}
        onClose={handleCloseFlagTransaction}
        transaction={selectedTransaction}
      />

      {/* Refund Transaction Modal */}
      <RefundTransactionModal
        isOpen={showRefundTransactionModal}
        onClose={handleCloseRefund}
        transaction={selectedTransaction}
      />
    </div>
  );
};

export default AgentDashboard;